/**
 * `wayai attachments …` — blobs hanging off a single record, as opposed to the
 * path-addressed `files` tree. Top-level
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { DATA_PROXY_PREFIX } from '@wayai/core/contracts';
import { toDataProxyPath } from '../../lib/api-client.js';
import { readFileSync } from 'node:fs';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, pathSegment, pathSegments, requireBase } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

interface AttachmentRow {
  key: string;
  size: number;
  uploaded: string;
}

/**
 * The attachment whose key ends in `<filename>`, or null.
 *
 * Keys are prefixed server-side, so the match is on the trailing segment(s)
 * after a `/` — anchored rather than a bare `includes`, so `guide.md` cannot
 * select `other-guide.md`.
 */
export function findAttachmentByFilename(
  attachments: readonly AttachmentRow[],
  filename: string,
): AttachmentRow | null {
  return attachments.find((a) => a.key.endsWith(`/${filename}`)) ?? null;
}

/**
 * The `/v1` path a returned `upload_url` designates.
 *
 * The value comes from the server, and the CLI puts the caller's WayAI session
 * on the request, so it must not be treated as an arbitrary address: only a
 * relative `/v1/…` path is accepted, which keeps the upload inside the
 * authenticated Data proxy. An absolute URL would need its own embedded
 * credential and is refused rather than sent our bearer token.
 */
export function uploadPathFrom(uploadUrl: unknown): string {
  if (typeof uploadUrl !== 'string') {
    throw expected(
      `The server returned an upload target this CLI will not send credentials to: ${String(uploadUrl)}`,
    );
  }
  // Assert on the PARSED result, not on the input string — the same shape the
  // backend proxy uses for the same reason (`apps/backend/src/rekor/upstream-url.ts`).
  //
  // Three iterations of this guard enumerated spellings the URL parser resolves
  // — `..`, then `%2e%2e`, then `\` — and each time the next spelling walked
  // through: the parser also strips ASCII tab, LF and CR before resolving, so
  // `.<TAB>.` becomes a `..` segment that no comparison of decoded segments can
  // see. Asking whether what comes OUT still sits under the proxy prefix cannot
  // be spelled around, because it runs the same parser `fetch` will.
  let resolved: URL;
  try {
    resolved = new URL(toDataProxyPath(uploadUrl), CONTAINMENT_ORIGIN);
  } catch {
    throw expected(
      `The server returned an upload target this CLI will not send credentials to: ${uploadUrl}`,
    );
  }
  if (resolved.origin !== CONTAINMENT_ORIGIN || !resolved.pathname.startsWith(`${DATA_PROXY_PREFIX}/`)) {
    throw expected(
      `The server returned an upload target this CLI will not send credentials to: ${uploadUrl}`,
    );
  }
  return uploadUrl;
}

/**
 * Origin used only to resolve the candidate path for the containment check. Any
 * absolute URL in the value replaces it, which is exactly what the origin
 * comparison then detects.
 */
const CONTAINMENT_ORIGIN = 'https://containment.invalid';

export function buildAttachmentsCommand(): Command {
  const attachments = new Command('attachments').description('Manage record attachments');

  attachments
    .command('upload <record_type> <id>')
    .description(
      'Get a presigned upload URL for a record attachment. With --file, uploads the file content in one step.',
    )
    .requiredOption('--filename <name>', 'File name or path (e.g. docs/guide.md)')
    .option('--content-type <type>', 'MIME type', 'application/octet-stream')
    .option('--file <path>', 'Local file to upload (skips presigned URL output, uploads directly)')
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      opts: { filename: string; contentType: string; file?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const data = await client.request<{ upload_url: string; [key: string]: unknown }>(
        'POST',
        `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${pathSegment(id, 'record id')}/attachments`,
        { filename: opts.filename, content_type: opts.contentType },
      );

      if (!opts.file) {
        printOutput(data, outputFormat(this));
        return;
      }
      const body = readFileSync(opts.file);
      await client.upload(uploadPathFrom(data?.upload_url), body, opts.contentType);
      printOutput({ ...data, uploaded: true }, outputFormat(this));
    });

  attachments
    .command('url <record_type> <id>')
    .description('Get a download URL for an attachment by filename')
    .requiredOption('--filename <name>', 'File name or path (e.g. docs/guide.md)')
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      opts: { filename: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const rows = await client.request<AttachmentRow[]>(
        'GET',
        `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${pathSegment(id, 'record id')}/attachments`,
      );
      const match = findAttachmentByFilename(rows ?? [], opts.filename);
      if (!match) {
        throw expected(`Attachment "${opts.filename}" not found`);
      }
      printOutput(
        { download_url: client.url(`/v1/${base}/attachments/${pathSegments(match.key, 'attachment key')}`), ...match },
        outputFormat(this),
      );
    });

  attachments
    .command('list <record_type> <id>')
    .description('List attachments for a record')
    .option('--prefix <path>', 'Filter by path prefix (e.g. docs/)')
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      opts: { prefix?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const query = opts.prefix ? `?prefix=${encodeURIComponent(opts.prefix)}` : '';
      printOutput(
        await client.request(
          'GET',
          `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${pathSegment(id, 'record id')}/attachments${query}`,
        ),
        outputFormat(this),
      );
    });

  attachments
    .command('delete <record_type> <id> <attachment-id>')
    .description('Delete an attachment')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (
      this: Command,
      recordType: string,
      id: string,
      attachmentId: string,
      opts: { yes?: boolean },
    ) {
      if (
        !opts.yes &&
        !(await confirm(
          `Delete attachment ${attachmentId} on ${recordType}/${id}? This cannot be undone.`,
        ))
      ) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request(
        'DELETE',
        `/v1/${base}/records/${pathSegment(recordType, 'record_type')}/${pathSegment(id, 'record id')}/attachments/${pathSegment(attachmentId, 'attachment id')}`,
      );
      console.log('Deleted');
    });

  return attachments;
}
